import { Endpoint } from './common'
import {
	createMutation,
	EnhancedMutationApi,
	MutationConfig,
	useBaseCancelOrder,
	useBaseRefundOrder,
	useBaseGetOrderReceipt,
	useBaseCreateCustomer,
	useBaseCreateGiftCard,
	useBaseUpdateGiftCard,
	useBaseCreateInvite,
	useBaseDeleteInvite,
	useBaseDeleteUser,
	useBaseUpdateUser,
	useBaseSetUserSignInCredentials,
	useBaseCreatePointOfSale,
	useBaseCreateProduct,
	useBaseCreateProductBarcode,
	useBaseCreateProductCategory,
	useBaseAddProductsToProductGroup,
	useBaseCreateProductGroup,
	useBaseCreateProductGroupFee,
	useBaseCreatePromotion,
	useBaseCreateSalesChannel,
	useBaseCreateSalesChannelType,
	useBaseUpdateSalesChannelType,
	useBaseDeleteSalesChannelType,
	useBaseCreateSalesChannelGroup,
	useBaseUpdateSalesChannelGroup,
	useBaseDeleteSalesChannelGroup,
	useBaseCreatePointOfSaleGroup,
	useBaseUpdatePointOfSaleGroup,
	useBaseDeletePointOfSaleGroup,
	useBaseDeleteProduct,
	useBaseDeleteProductBarcode,
	useBaseDeleteProductGroup,
	useBaseDeleteProductGroupFee,
	useBaseDeletePromotion,
	useBaseDeleteSalesChannel,
	useBaseExportOrders,
	useBaseImportProducts,
	useBaseResetBusinessCardPin,
	useBaseUpdateBusinessCard,
	useBaseUpdateLoyaltyCard,
	useBaseDeleteLoyaltyCard,
	useBaseUpdatePointOfSale,
	useBaseSetPointOfSaleUnlockPin,
	useBaseUpdateProductGroupsForPointOfSale,
	useBaseUpdateProduct,
	useBaseUpdateProductCategory,
	useBaseDeleteProductCategory,
	useBaseUpdateProductGroup,
	useBaseSetProductGroupImage,
	useBaseDeleteProductGroupImage,
	useBaseSetProductCategoryImage,
	useBaseDeleteProductCategoryImage,
	useBaseUpdateProductGroupFee,
	useBaseUpdateProductOrganization,
	useBaseUpdateProductPricing,
	useBaseUpdatePromotion,
	useBaseValidateProductImport,
	useBaseUpdateCustomer,
	useBaseCreateCompany,
	useBaseUpdateCompany,
	useBaseDeleteCompany,
	useBaseCreateSettlement,
	useBaseMarkSettlementSettled,
	useBaseUpdateMe,
	useBaseCreatePublishableKey,
	useBaseUpdatePublishableKey,
	useBaseDeletePublishableKey,
	useBaseCreateSecretKey,
	useBaseUpdateSecretKey,
	useBaseUpdateSalesChannel,
	useBaseAttachProductImage,
	useBaseReorderProductImages,
	useBaseDeleteProductImage,
	useBaseSetPrimaryProductImage,
	useBaseInitAssetUpload,
	useBaseCompleteAssetUpload,
	useBaseUpdateAsset,
	useBaseDeleteAsset,
	useBaseRemoveTagFromAssets,
	useBaseCreateAssetTag,
	useBaseRenameAssetTag,
	useBaseCreateMarketingChannel,
	useBaseUpdateMarketingChannel,
	useBaseDeleteMarketingChannel,
	useBaseReplaceFeedLayoutBlocks,
	useBaseReplaceChannelLayout,
	useBaseCreateMarketingContent,
	useBaseUpdateMarketingContent,
	useBaseDeleteMarketingContent,
	useBaseReplaceMarketingContentBlocks,
	useBaseDuplicateMarketingContent,
	useBaseAdjustInventoryLevel,
	useBaseUpdateInventoryLevel,
	useBaseUpdateInventorySettings,
	useBaseCreateInventoryLocation,
	useBaseUpdateInventoryLocation,
	useBaseProvisionInventoryLocationLevels,
	useBaseCreateSupplier,
	useBaseUpdateSupplier,
	useBaseReceivePurchaseOrder,
	useBaseCreatePurchaseOrder,
	useBaseCreateReplenishmentPurchaseOrders,
	useBaseMarkPurchaseOrderOrdered,
	useBaseCancelPurchaseOrder,
	useBaseShipInventoryTransfer,
	useBaseReceiveInventoryTransfer,
	useBaseCreateInventoryTransfer,
	useBaseCancelInventoryTransfer,
	useBaseUpsertVariantSupplier,
	useBaseSetPreferredVariantSupplier,
	useBaseDeleteVariantSupplier,
	useBaseUpdateProductVariant,
	useBaseCreateTeyaConnectUrl,
	useBaseDisconnectTeya,
	useBaseRegisterRapydDevice,
	useBasePutIntegrationGlobalConfig,
	useBaseCreateIntegrationOverride,
	useBaseUpdateIntegrationOverride,
	useBaseDeleteIntegrationOverride,
	useBaseRefreshPointOfSaleDeviceStatus,
	useBaseNotifyPointOfSaleAppUpdate,
	useBaseRestartPointOfSaleApp,
	useBaseLockPointOfSaleKiosk,
	useBaseCreatePointOfSaleGmsEnrollment,
	useBaseSyncPointOfSaleGmsPolicy,
	useBaseRebootPointOfSaleGmsDevice,
	useBaseDeletePointOfSaleGmsDevice,
	useBaseEnrollPointOfSaleDevice,
	useBaseUnenrollPointOfSaleDevice,
	useBaseAcceptBillingPlan
} from './react-query'

export const useCreateInvite = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInvite>
): EnhancedMutationApi<Endpoint.CreateInvite, Subscribe> => {
	return useBaseCreateInvite(
		subscribe,
		config,
		createMutation<Endpoint.CreateInvite, Endpoint.ListInvites>(Endpoint.ListInvites, () => ({
			wildcard: true,
			key: [Endpoint.ListInvites]
		}))
	)
}

export const useDeleteUser = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteUser>
): EnhancedMutationApi<Endpoint.DeleteUser, Subscribe> => {
	return useBaseDeleteUser(
		subscribe,
		config,
		createMutation<Endpoint.DeleteUser, Endpoint.ListUsers>(Endpoint.ListUsers, () => ({
			wildcard: true,
			key: [Endpoint.ListUsers]
		}))
	)
}

export const useUpdateUser = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateUser>
): EnhancedMutationApi<Endpoint.UpdateUser, Subscribe> => {
	return useBaseUpdateUser(
		subscribe,
		config,
		createMutation<Endpoint.UpdateUser, Endpoint.ListUsers>(Endpoint.ListUsers, () => ({
			wildcard: true,
			key: [Endpoint.ListUsers]
		})),
		createMutation<Endpoint.UpdateUser, Endpoint.GetUser>(Endpoint.GetUser, ({ path: { userUuid } }) => ({
			path: { userUuid }
		}))
	)
}

export const useSetUserSignInCredentials = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetUserSignInCredentials>
): EnhancedMutationApi<Endpoint.SetUserSignInCredentials, Subscribe> => {
	return useBaseSetUserSignInCredentials(
		subscribe,
		config,
		createMutation<Endpoint.SetUserSignInCredentials, Endpoint.GetUserSignInCredentials>(
			Endpoint.GetUserSignInCredentials,
			() => ({
				wildcard: true,
				key: [Endpoint.GetUserSignInCredentials]
			})
		)
	)
}

export const useDeleteInvite = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteInvite>
): EnhancedMutationApi<Endpoint.DeleteInvite, Subscribe> => {
	return useBaseDeleteInvite(
		subscribe,
		config,
		createMutation<Endpoint.DeleteInvite, Endpoint.ListInvites>(Endpoint.ListInvites, () => ({
			wildcard: true,
			key: [Endpoint.ListInvites]
		}))
	)
}

export const useSetPointOfSaleUnlockPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetPointOfSaleUnlockPin>
): EnhancedMutationApi<Endpoint.SetPointOfSaleUnlockPin, Subscribe> => {
	return useBaseSetPointOfSaleUnlockPin(
		subscribe,
		config,
		createMutation<Endpoint.SetPointOfSaleUnlockPin, Endpoint.GetPointOfSaleUnlockPin>(
			Endpoint.GetPointOfSaleUnlockPin,
			() => ({
				wildcard: true,
				key: [Endpoint.GetPointOfSaleUnlockPin]
			})
		)
	)
}

export const useCreateProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProduct>
): EnhancedMutationApi<Endpoint.CreateProduct, Subscribe> => {
	return useBaseCreateProduct(
		subscribe,
		config,
		createMutation<Endpoint.CreateProduct, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		}))
	)
}

export const useCreateProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductCategory>
): EnhancedMutationApi<Endpoint.CreateProductCategory, Subscribe> => {
	return useBaseCreateProductCategory(
		subscribe,
		config,
		createMutation<Endpoint.CreateProductCategory, Endpoint.ListProductCategories>(
			Endpoint.ListProductCategories,
			() => ({
				wildcard: true,
				key: [Endpoint.ListProductCategories]
			})
		)
	)
}

export const useUpdateProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductCategory>
): EnhancedMutationApi<Endpoint.UpdateProductCategory, Subscribe> => {
	return useBaseUpdateProductCategory(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductCategory, Endpoint.ListProductCategories>(
			Endpoint.ListProductCategories,
			() => ({
				wildcard: true,
				key: [Endpoint.ListProductCategories]
			})
		)
	)
}

export const useDeleteProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductCategory>
): EnhancedMutationApi<Endpoint.DeleteProductCategory, Subscribe> => {
	return useBaseDeleteProductCategory(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProductCategory, Endpoint.ListProductCategories>(
			Endpoint.ListProductCategories,
			() => ({
				wildcard: true,
				key: [Endpoint.ListProductCategories]
			})
		),
		createMutation<Endpoint.DeleteProductCategory, Endpoint.GetProductCategoryHierarchy>(
			Endpoint.GetProductCategoryHierarchy,
			() => ({
				wildcard: true,
				key: [Endpoint.GetProductCategoryHierarchy]
			})
		)
	)
}

export const useCreatePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePromotion>
): EnhancedMutationApi<Endpoint.CreatePromotion, Subscribe> => {
	return useBaseCreatePromotion(
		subscribe,
		config,
		createMutation<Endpoint.CreatePromotion, Endpoint.ListPromotions>(Endpoint.ListPromotions, () => ({
			wildcard: true,
			key: [Endpoint.ListPromotions]
		}))
	)
}

export const useUpdatePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePromotion>
): EnhancedMutationApi<Endpoint.UpdatePromotion, Subscribe> => {
	return useBaseUpdatePromotion(
		subscribe,
		config,
		createMutation<Endpoint.UpdatePromotion, Endpoint.ListPromotions>(Endpoint.ListPromotions, () => ({
			wildcard: true,
			key: [Endpoint.ListPromotions]
		})),
		createMutation<Endpoint.UpdatePromotion, Endpoint.GetPromotion>(
			Endpoint.GetPromotion,
			({ path: { promotionUuid } }) => ({ path: { promotionUuid } })
		)
	)
}

export const useDeletePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePromotion>
): EnhancedMutationApi<Endpoint.DeletePromotion, Subscribe> => {
	return useBaseDeletePromotion(
		subscribe,
		config,
		createMutation<Endpoint.DeletePromotion, Endpoint.ListPromotions>(Endpoint.ListPromotions, () => ({
			wildcard: true,
			key: [Endpoint.ListPromotions]
		})),
		createMutation<Endpoint.DeletePromotion, Endpoint.GetPromotion>(
			Endpoint.GetPromotion,
			({ path: { promotionUuid } }) => ({ path: { promotionUuid } })
		)
	)
}

export const useCreateSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannel>
): EnhancedMutationApi<Endpoint.CreateSalesChannel, Subscribe> => {
	return useBaseCreateSalesChannel(
		subscribe,
		config,
		createMutation<Endpoint.CreateSalesChannel, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		}))
	)
}

export const useDeleteSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannel>
): EnhancedMutationApi<Endpoint.DeleteSalesChannel, Subscribe> => {
	return useBaseDeleteSalesChannel(
		subscribe,
		config,
		createMutation<Endpoint.DeleteSalesChannel, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		}))
	)
}

export const useUpdateSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannel>
): EnhancedMutationApi<Endpoint.UpdateSalesChannel, Subscribe> => {
	return useBaseUpdateSalesChannel(
		subscribe,
		config,
		createMutation<Endpoint.UpdateSalesChannel, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		})),
		createMutation<Endpoint.UpdateSalesChannel, Endpoint.GetSalesChannel>(
			Endpoint.GetSalesChannel,
			({ path: { salesChannelUuid } }) => ({ path: { salesChannelUuid } })
		)
	)
}

export const useCreateSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannelType>
): EnhancedMutationApi<Endpoint.CreateSalesChannelType, Subscribe> => {
	return useBaseCreateSalesChannelType(
		subscribe,
		config,
		createMutation<Endpoint.CreateSalesChannelType, Endpoint.ListSalesChannelTypes>(
			Endpoint.ListSalesChannelTypes,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelTypes]
			})
		)
	)
}

export const useUpdateSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannelType>
): EnhancedMutationApi<Endpoint.UpdateSalesChannelType, Subscribe> => {
	return useBaseUpdateSalesChannelType(
		subscribe,
		config,
		createMutation<Endpoint.UpdateSalesChannelType, Endpoint.ListSalesChannelTypes>(
			Endpoint.ListSalesChannelTypes,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelTypes]
			})
		),
		createMutation<Endpoint.UpdateSalesChannelType, Endpoint.GetSalesChannelType>(
			Endpoint.GetSalesChannelType,
			({ path: { salesChannelTypeUuid } }) => ({ path: { salesChannelTypeUuid } })
		)
	)
}

export const useDeleteSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannelType>
): EnhancedMutationApi<Endpoint.DeleteSalesChannelType, Subscribe> => {
	return useBaseDeleteSalesChannelType(
		subscribe,
		config,
		createMutation<Endpoint.DeleteSalesChannelType, Endpoint.ListSalesChannelTypes>(
			Endpoint.ListSalesChannelTypes,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelTypes]
			})
		)
	)
}

export const useCreateSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannelGroup>
): EnhancedMutationApi<Endpoint.CreateSalesChannelGroup, Subscribe> => {
	return useBaseCreateSalesChannelGroup(
		subscribe,
		config,
		createMutation<Endpoint.CreateSalesChannelGroup, Endpoint.ListSalesChannelGroups>(
			Endpoint.ListSalesChannelGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelGroups]
			})
		)
	)
}

export const useUpdateSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannelGroup>
): EnhancedMutationApi<Endpoint.UpdateSalesChannelGroup, Subscribe> => {
	return useBaseUpdateSalesChannelGroup(
		subscribe,
		config,
		createMutation<Endpoint.UpdateSalesChannelGroup, Endpoint.ListSalesChannelGroups>(
			Endpoint.ListSalesChannelGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelGroups]
			})
		),
		createMutation<Endpoint.UpdateSalesChannelGroup, Endpoint.GetSalesChannelGroup>(
			Endpoint.GetSalesChannelGroup,
			({ path: { salesChannelGroupUuid } }) => ({ path: { salesChannelGroupUuid } })
		),
		// Membership changes are reflected on the sales channel list (group column).
		createMutation<Endpoint.UpdateSalesChannelGroup, Endpoint.ListSalesChannels>(
			Endpoint.ListSalesChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannels]
			})
		)
	)
}

export const useDeleteSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannelGroup>
): EnhancedMutationApi<Endpoint.DeleteSalesChannelGroup, Subscribe> => {
	return useBaseDeleteSalesChannelGroup(
		subscribe,
		config,
		createMutation<Endpoint.DeleteSalesChannelGroup, Endpoint.ListSalesChannelGroups>(
			Endpoint.ListSalesChannelGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannelGroups]
			})
		),
		createMutation<Endpoint.DeleteSalesChannelGroup, Endpoint.ListSalesChannels>(
			Endpoint.ListSalesChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannels]
			})
		),
		createMutation<Endpoint.DeleteSalesChannelGroup, Endpoint.GetSalesChannelGroup>(
			Endpoint.GetSalesChannelGroup,
			({ path: { salesChannelGroupUuid } }) => ({ path: { salesChannelGroupUuid } })
		)
	)
}

export const useCreatePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSaleGroup>
): EnhancedMutationApi<Endpoint.CreatePointOfSaleGroup, Subscribe> => {
	return useBaseCreatePointOfSaleGroup(
		subscribe,
		config,
		createMutation<Endpoint.CreatePointOfSaleGroup, Endpoint.ListPointOfSaleGroups>(
			Endpoint.ListPointOfSaleGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPointOfSaleGroups]
			})
		)
	)
}

export const useUpdatePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePointOfSaleGroup>
): EnhancedMutationApi<Endpoint.UpdatePointOfSaleGroup, Subscribe> => {
	return useBaseUpdatePointOfSaleGroup(
		subscribe,
		config,
		createMutation<Endpoint.UpdatePointOfSaleGroup, Endpoint.ListPointOfSaleGroups>(
			Endpoint.ListPointOfSaleGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPointOfSaleGroups]
			})
		),
		createMutation<Endpoint.UpdatePointOfSaleGroup, Endpoint.GetPointOfSaleGroup>(
			Endpoint.GetPointOfSaleGroup,
			({ path: { pointOfSaleGroupUuid } }) => ({ path: { pointOfSaleGroupUuid } })
		),
		// Membership changes are reflected on the points-of-sale list (group column).
		createMutation<Endpoint.UpdatePointOfSaleGroup, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		}))
	)
}

export const useDeletePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePointOfSaleGroup>
): EnhancedMutationApi<Endpoint.DeletePointOfSaleGroup, Subscribe> => {
	return useBaseDeletePointOfSaleGroup(
		subscribe,
		config,
		createMutation<Endpoint.DeletePointOfSaleGroup, Endpoint.ListPointOfSaleGroups>(
			Endpoint.ListPointOfSaleGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPointOfSaleGroups]
			})
		),
		createMutation<Endpoint.DeletePointOfSaleGroup, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		})),
		createMutation<Endpoint.DeletePointOfSaleGroup, Endpoint.GetPointOfSaleGroup>(
			Endpoint.GetPointOfSaleGroup,
			({ path: { pointOfSaleGroupUuid } }) => ({ path: { pointOfSaleGroupUuid } })
		)
	)
}

export const useCreatePointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSale>
): EnhancedMutationApi<Endpoint.CreatePointOfSale, Subscribe> => {
	return useBaseCreatePointOfSale(
		subscribe,
		config,
		createMutation<Endpoint.CreatePointOfSale, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		}))
	)
}

export const useUpdatePointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePointOfSale>
): EnhancedMutationApi<Endpoint.UpdatePointOfSale, Subscribe> => {
	return useBaseUpdatePointOfSale(
		subscribe,
		config,
		createMutation<Endpoint.UpdatePointOfSale, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		})),
		createMutation<Endpoint.UpdatePointOfSale, Endpoint.GetPointOfSale>(
			Endpoint.GetPointOfSale,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useUpdateProductGroupsForPointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroupsForPointOfSale>
): EnhancedMutationApi<Endpoint.UpdateProductGroupsForPointOfSale, Subscribe> => {
	return useBaseUpdateProductGroupsForPointOfSale(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductGroupsForPointOfSale, Endpoint.ListProductGroupsForPointOfSale>(
			Endpoint.ListProductGroupsForPointOfSale,
			() => ({
				wildcard: true,
				key: [Endpoint.ListProductGroupsForPointOfSale]
			})
		)
	)
}

export const useUpdateProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProduct>
): EnhancedMutationApi<Endpoint.UpdateProduct, Subscribe> => {
	return useBaseUpdateProduct(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProduct>(Endpoint.ListProducts, ({ path }) => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		})),
		createMutation<Endpoint.UpdateProduct, Endpoint.ListProductBarcodes>(
			Endpoint.ListProductBarcodes,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		),
		createMutation<Endpoint.UpdateProduct, Endpoint.GetProduct>(
			Endpoint.GetProduct,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		)
	)
}

export const useDeleteProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProduct>
): EnhancedMutationApi<Endpoint.DeleteProduct, Subscribe> => {
	return useBaseDeleteProduct(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProduct, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		}))
	)
}

export const useUpdateProductOrganization = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductOrganization>
): EnhancedMutationApi<Endpoint.UpdateProductOrganization, Subscribe> => {
	return useBaseUpdateProductOrganization(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductOrganization, Endpoint.GetProduct>(
			Endpoint.GetProduct,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		),
		createMutation<Endpoint.UpdateProductOrganization, Endpoint.GetProductOrganization>(
			Endpoint.GetProductOrganization,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		)
	)
}

export const useCreateProductBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductBarcode>
): EnhancedMutationApi<Endpoint.CreateProductBarcode, Subscribe> => {
	return useBaseCreateProductBarcode(
		subscribe,
		config,
		createMutation<Endpoint.CreateProductBarcode, Endpoint.ListProductBarcodes>(
			Endpoint.ListProductBarcodes,
			({ path }) => ({ path })
		)
	)
}

export const useDeleteProductBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductBarcode>
): EnhancedMutationApi<Endpoint.DeleteProductBarcode, Subscribe> => {
	return useBaseDeleteProductBarcode(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProductBarcode, Endpoint.ListProductBarcodes>(
			Endpoint.ListProductBarcodes,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		)
	)
}

export const useImportProducts = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ImportProducts>
): EnhancedMutationApi<Endpoint.ImportProducts, Subscribe> => {
	return useBaseImportProducts(
		subscribe,
		config,
		createMutation<Endpoint.ImportProducts, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		}))
	)
}

export const useValidateProductImport = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ValidateProductImport>
): EnhancedMutationApi<Endpoint.ValidateProductImport, Subscribe> => {
	return useBaseValidateProductImport(subscribe, config)
}

export const useExportOrders = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ExportOrders>
): EnhancedMutationApi<Endpoint.ExportOrders, Subscribe> => {
	return useBaseExportOrders(subscribe, config)
}

export const useGetOrderReceipt = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.GetOrderReceipt>
): EnhancedMutationApi<Endpoint.GetOrderReceipt, Subscribe> => {
	return useBaseGetOrderReceipt(subscribe, config)
}

export const useRefundOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RefundOrder>
): EnhancedMutationApi<Endpoint.RefundOrder, Subscribe> => {
	return useBaseRefundOrder(
		subscribe,
		config,
		createMutation<Endpoint.RefundOrder, Endpoint.GetOrder>(Endpoint.GetOrder, ({ path: { orderUuid } }) => ({
			path: { orderUuid }
		}))
	)
}

export const useCancelOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelOrder>
): EnhancedMutationApi<Endpoint.CancelOrder, Subscribe> => {
	return useBaseCancelOrder(
		subscribe,
		config,
		createMutation<Endpoint.CancelOrder, Endpoint.GetOrder>(Endpoint.GetOrder, ({ path: { orderUuid } }) => ({
			path: { orderUuid }
		})),
		createMutation<Endpoint.CancelOrder, Endpoint.ListOrders>(Endpoint.ListOrders, () => ({
			wildcard: true,
			key: [Endpoint.ListOrders]
		}))
	)
}

export const useUpdateCustomer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCustomer>
): EnhancedMutationApi<Endpoint.UpdateCustomer, Subscribe> => {
	return useBaseUpdateCustomer(
		subscribe,
		config,
		createMutation<Endpoint.UpdateCustomer, Endpoint.GetCustomer>(
			Endpoint.GetCustomer,
			({ path: { customerUuid } }) => ({
				path: { customerUuid }
			})
		),
		createMutation<Endpoint.UpdateCustomer, Endpoint.ListCustomers>(Endpoint.ListCustomers, () => ({
			wildcard: true,
			key: [Endpoint.ListCustomers]
		}))
	)
}

export const useCreateCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateCompany>
): EnhancedMutationApi<Endpoint.CreateCompany, Subscribe> => {
	return useBaseCreateCompany(
		subscribe,
		config,
		createMutation<Endpoint.CreateCompany, Endpoint.ListCompanies>(Endpoint.ListCompanies, () => ({
			wildcard: true,
			key: [Endpoint.ListCompanies]
		}))
	)
}

export const useUpdateCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCompany>
): EnhancedMutationApi<Endpoint.UpdateCompany, Subscribe> => {
	return useBaseUpdateCompany(
		subscribe,
		config,
		createMutation<Endpoint.UpdateCompany, Endpoint.GetCompany>(
			Endpoint.GetCompany,
			({ path: { companyUuid } }) => ({
				path: { companyUuid }
			})
		),
		createMutation<Endpoint.UpdateCompany, Endpoint.ListCompanies>(Endpoint.ListCompanies, () => ({
			wildcard: true,
			key: [Endpoint.ListCompanies]
		}))
	)
}

export const useDeleteCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteCompany>
): EnhancedMutationApi<Endpoint.DeleteCompany, Subscribe> => {
	return useBaseDeleteCompany(
		subscribe,
		config,
		createMutation<Endpoint.DeleteCompany, Endpoint.ListCompanies>(Endpoint.ListCompanies, () => ({
			wildcard: true,
			key: [Endpoint.ListCompanies]
		}))
	)
}

export const useCreateSettlement = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSettlement>
): EnhancedMutationApi<Endpoint.CreateSettlement, Subscribe> => {
	return useBaseCreateSettlement(
		subscribe,
		config,
		createMutation<Endpoint.CreateSettlement, Endpoint.ListCompanySettlements>(
			Endpoint.ListCompanySettlements,
			({ path: { companyUuid } }) => ({
				path: { companyUuid },
				query: {}
			})
		),
		createMutation<Endpoint.CreateSettlement, Endpoint.GetCompany>(
			Endpoint.GetCompany,
			({ path: { companyUuid } }) => ({
				path: { companyUuid }
			})
		)
	)
}

export const useMarkSettlementSettled = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.MarkSettlementSettled>
): EnhancedMutationApi<Endpoint.MarkSettlementSettled, Subscribe> => {
	return useBaseMarkSettlementSettled(
		subscribe,
		config,
		createMutation<Endpoint.MarkSettlementSettled, Endpoint.GetSettlement>(
			Endpoint.GetSettlement,
			({ path: { settlementUuid } }) => ({
				path: { settlementUuid }
			})
		)
	)
}

export const useUpdateProductPricing = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductPricing>
): EnhancedMutationApi<Endpoint.UpdateProductPricing, Subscribe> => {
	return useBaseUpdateProductPricing(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductPricing, Endpoint.GetProduct>(
			Endpoint.GetProduct,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		)
	)
}

export const useCreateProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductGroup>
): EnhancedMutationApi<Endpoint.CreateProductGroup, Subscribe> => {
	return useBaseCreateProductGroup(
		subscribe,
		config,
		createMutation<Endpoint.CreateProductGroup, Endpoint.ListProductGroups>(Endpoint.ListProductGroups, () => ({
			wildcard: true,
			key: [Endpoint.ListProductGroups]
		}))
	)
}

export const useUpdateProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroup>
): EnhancedMutationApi<Endpoint.UpdateProductGroup, Subscribe> => {
	return useBaseUpdateProductGroup(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductGroup>(Endpoint.ListProductGroups, ({ path }) => ({
			wildcard: true,
			key: [Endpoint.ListProductGroups]
		})),
		createMutation<Endpoint.UpdateProductGroup, Endpoint.GetProductGroup>(
			Endpoint.GetProductGroup,
			({ path: { groupUuid } }) => ({ path: { groupUuid } })
		)
	)
}

export const useDeleteProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroup>
): EnhancedMutationApi<Endpoint.DeleteProductGroup, Subscribe> => {
	return useBaseDeleteProductGroup(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProductGroup, Endpoint.ListProductGroups>(Endpoint.ListProductGroups, () => ({
			wildcard: true,
			key: [Endpoint.ListProductGroups]
		}))
	)
}

export const useAddProductsToProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AddProductsToProductGroup>
): EnhancedMutationApi<Endpoint.AddProductsToProductGroup, Subscribe> => {
	return useBaseAddProductsToProductGroup(
		subscribe,
		config,
		// The group's member table is a ListProducts query filtered by groupUuids, and the group list
		// shows each group's product count, so both have to go.
		createMutation<Endpoint.AddProductsToProductGroup, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		})),
		createMutation<Endpoint.AddProductsToProductGroup, Endpoint.ListProductGroups>(
			Endpoint.ListProductGroups,
			() => ({
				wildcard: true,
				key: [Endpoint.ListProductGroups]
			})
		),
		createMutation<Endpoint.AddProductsToProductGroup, Endpoint.GetProductGroup>(
			Endpoint.GetProductGroup,
			({ path: { groupUuid } }) => ({ path: { groupUuid } })
		)
	)
}

const productGroupImageMutations = <E extends Endpoint.SetProductGroupImage | Endpoint.DeleteProductGroupImage>() => [
	createMutation<E, Endpoint.GetProductGroup>(Endpoint.GetProductGroup, ({ path: { groupUuid } }) => ({
		path: { groupUuid }
	})),
	createMutation<E, Endpoint.ListProductGroups>(Endpoint.ListProductGroups, () => ({
		wildcard: true,
		key: [Endpoint.ListProductGroups]
	})),
	createMutation<E, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
		wildcard: true,
		key: [Endpoint.ListAssets]
	}))
]

export const useSetProductGroupImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetProductGroupImage>
): EnhancedMutationApi<Endpoint.SetProductGroupImage, Subscribe> => {
	return useBaseSetProductGroupImage(
		subscribe,
		config,
		...productGroupImageMutations<Endpoint.SetProductGroupImage>()
	)
}

export const useDeleteProductGroupImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroupImage>
): EnhancedMutationApi<Endpoint.DeleteProductGroupImage, Subscribe> => {
	return useBaseDeleteProductGroupImage(
		subscribe,
		config,
		...productGroupImageMutations<Endpoint.DeleteProductGroupImage>()
	)
}

const productCategoryImageMutations = <
	E extends Endpoint.SetProductCategoryImage | Endpoint.DeleteProductCategoryImage
>() => [
	createMutation<E, Endpoint.GetProductCategory>(Endpoint.GetProductCategory, ({ path: { categoryUuid } }) => ({
		path: { categoryUuid }
	})),
	createMutation<E, Endpoint.ListProductCategories>(Endpoint.ListProductCategories, () => ({
		wildcard: true,
		key: [Endpoint.ListProductCategories]
	})),
	createMutation<E, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
		wildcard: true,
		key: [Endpoint.ListAssets]
	}))
]

export const useSetProductCategoryImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetProductCategoryImage>
): EnhancedMutationApi<Endpoint.SetProductCategoryImage, Subscribe> => {
	return useBaseSetProductCategoryImage(
		subscribe,
		config,
		...productCategoryImageMutations<Endpoint.SetProductCategoryImage>()
	)
}

export const useDeleteProductCategoryImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductCategoryImage>
): EnhancedMutationApi<Endpoint.DeleteProductCategoryImage, Subscribe> => {
	return useBaseDeleteProductCategoryImage(
		subscribe,
		config,
		...productCategoryImageMutations<Endpoint.DeleteProductCategoryImage>()
	)
}

export const useCreateProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductGroupFee>
): EnhancedMutationApi<Endpoint.CreateProductGroupFee, Subscribe> => {
	return useBaseCreateProductGroupFee(
		subscribe,
		config,
		createMutation<Endpoint.CreateProductGroupFee, Endpoint.GetProductGroup>(
			Endpoint.GetProductGroup,
			({ path: { groupUuid } }) => ({ path: { groupUuid } })
		)
	)
}

export const useDeleteProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroupFee>
): EnhancedMutationApi<Endpoint.DeleteProductGroupFee, Subscribe> => {
	return useBaseDeleteProductGroupFee(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProductGroupFee, Endpoint.GetProductGroup>(
			Endpoint.GetProductGroup,
			({ path: { groupUuid } }) => ({ path: { groupUuid } })
		)
	)
}

export const useUpdateProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroupFee>
): EnhancedMutationApi<Endpoint.UpdateProductGroupFee, Subscribe> => {
	return useBaseUpdateProductGroupFee(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductGroupFee, Endpoint.GetProductGroup>(
			Endpoint.GetProductGroup,
			({ path: { groupUuid } }) => ({ path: { groupUuid } })
		)
	)
}

export const useCreateCustomer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateCustomer>
): EnhancedMutationApi<Endpoint.CreateCustomer, Subscribe> => {
	return useBaseCreateCustomer(
		subscribe,
		config,
		createMutation<Endpoint.CreateCustomer, Endpoint.ListCustomers>(Endpoint.ListCustomers, () => ({
			wildcard: true,
			key: [Endpoint.ListCustomers]
		}))
	)
}

export const useUpdateBusinessCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	opts?: { customerUuid?: string; companyUuid?: string }
): EnhancedMutationApi<Endpoint.UpdateBusinessCard, Subscribe> => {
	return useBaseUpdateBusinessCard(
		subscribe,
		undefined,
		createMutation<Endpoint.UpdateBusinessCard, Endpoint.ListBusinessCards>(Endpoint.ListBusinessCards, () => ({
			wildcard: true,
			key: [Endpoint.ListBusinessCards]
		})),
		createMutation<Endpoint.UpdateBusinessCard, Endpoint.GetBusinessCard>(
			Endpoint.GetBusinessCard,
			({ path: { cardUuid } }) => ({ path: { cardUuid } })
		),
		...(opts?.customerUuid
			? [
					createMutation<Endpoint.UpdateBusinessCard, Endpoint.GetCustomer>(Endpoint.GetCustomer, () => ({
						path: { customerUuid: opts.customerUuid! }
					}))
				]
			: []),
		...(opts?.companyUuid
			? [
					createMutation<Endpoint.UpdateBusinessCard, Endpoint.GetCompany>(Endpoint.GetCompany, () => ({
						path: { companyUuid: opts.companyUuid! }
					}))
				]
			: [])
	)
}

export const useResetBusinessCardPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ResetBusinessCardPin>
): EnhancedMutationApi<Endpoint.ResetBusinessCardPin, Subscribe> => {
	return useBaseResetBusinessCardPin(
		subscribe,
		config,
		createMutation<Endpoint.ResetBusinessCardPin, Endpoint.ListBusinessCards>(Endpoint.ListBusinessCards, () => ({
			wildcard: true,
			key: [Endpoint.ListBusinessCards]
		}))
	)
}

export const useCreateGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateGiftCard>
): EnhancedMutationApi<Endpoint.CreateGiftCard, Subscribe> => {
	return useBaseCreateGiftCard(
		subscribe,
		config,
		createMutation<Endpoint.CreateGiftCard, Endpoint.ListGiftCards>(Endpoint.ListGiftCards, () => ({
			wildcard: true,
			key: [Endpoint.ListGiftCards]
		}))
	)
}

export const useUpdateMe = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateMe>
): EnhancedMutationApi<Endpoint.UpdateMe, Subscribe> => {
	return useBaseUpdateMe(
		subscribe,
		config,
		createMutation<Endpoint.UpdateMe, Endpoint.GetMe>(Endpoint.GetMe, () => ({}))
	)
}

export const useAcceptBillingPlan = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AcceptBillingPlan>
): EnhancedMutationApi<Endpoint.AcceptBillingPlan, Subscribe> => {
	return useBaseAcceptBillingPlan(
		subscribe,
		config,
		createMutation<Endpoint.AcceptBillingPlan, Endpoint.GetBillingPlan>(Endpoint.GetBillingPlan, () => ({}))
	)
}

export const useCreatePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePublishableKey>
): EnhancedMutationApi<Endpoint.CreatePublishableKey, Subscribe> => {
	return useBaseCreatePublishableKey(
		subscribe,
		config,
		createMutation<Endpoint.CreatePublishableKey, Endpoint.GetSalesChannel>(Endpoint.GetSalesChannel, () => ({
			wildcard: true,
			key: [Endpoint.GetSalesChannel]
		})),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.GetPointOfSale>(Endpoint.GetPointOfSale, () => ({
			wildcard: true,
			key: [Endpoint.GetPointOfSale]
		})),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.ListPublishableKeys>(
			Endpoint.ListPublishableKeys,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPublishableKeys]
			})
		),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		})),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		})),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingChannels] })
		),
		createMutation<Endpoint.CreatePublishableKey, Endpoint.GetMarketingChannel>(
			Endpoint.GetMarketingChannel,
			() => ({ wildcard: true, key: [Endpoint.GetMarketingChannel] })
		)
	)
}

export const useUpdatePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePublishableKey>
): EnhancedMutationApi<Endpoint.UpdatePublishableKey, Subscribe> => {
	return useBaseUpdatePublishableKey(
		subscribe,
		config,
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.ListPublishableKeys>(
			Endpoint.ListPublishableKeys,
			() => ({ wildcard: true, key: [Endpoint.ListPublishableKeys] })
		),
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.GetPublishableKey>(
			Endpoint.GetPublishableKey,
			({ path: { publishableKeyUuid } }) => ({ path: { publishableKeyUuid } })
		),
		// Links changed, so sales channel and marketing channel views may show stale key associations.
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.GetSalesChannel>(Endpoint.GetSalesChannel, () => ({
			wildcard: true,
			key: [Endpoint.GetSalesChannel]
		})),
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		})),
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingChannels] })
		),
		createMutation<Endpoint.UpdatePublishableKey, Endpoint.GetMarketingChannel>(
			Endpoint.GetMarketingChannel,
			() => ({ wildcard: true, key: [Endpoint.GetMarketingChannel] })
		)
	)
}

export const useDeletePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePublishableKey>
): EnhancedMutationApi<Endpoint.DeletePublishableKey, Subscribe> => {
	return useBaseDeletePublishableKey(
		subscribe,
		config,
		createMutation<Endpoint.DeletePublishableKey, Endpoint.ListPublishableKeys>(
			Endpoint.ListPublishableKeys,
			() => ({ wildcard: true, key: [Endpoint.ListPublishableKeys] })
		),
		createMutation<Endpoint.DeletePublishableKey, Endpoint.GetSalesChannel>(Endpoint.GetSalesChannel, () => ({
			wildcard: true,
			key: [Endpoint.GetSalesChannel]
		})),
		createMutation<Endpoint.DeletePublishableKey, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		})),
		createMutation<Endpoint.DeletePublishableKey, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingChannels] })
		),
		createMutation<Endpoint.DeletePublishableKey, Endpoint.GetMarketingChannel>(
			Endpoint.GetMarketingChannel,
			() => ({ wildcard: true, key: [Endpoint.GetMarketingChannel] })
		)
	)
}

export const useCreateSecretKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSecretKey>
): EnhancedMutationApi<Endpoint.CreateSecretKey, Subscribe> => {
	return useBaseCreateSecretKey(
		subscribe,
		config,
		createMutation<Endpoint.CreateSecretKey, Endpoint.GetSalesChannel>(Endpoint.GetSalesChannel, () => ({
			wildcard: true,
			key: [Endpoint.GetSalesChannel]
		})),
		createMutation<Endpoint.CreateSecretKey, Endpoint.GetPointOfSale>(Endpoint.GetPointOfSale, () => ({
			wildcard: true,
			key: [Endpoint.GetPointOfSale]
		})),
		createMutation<Endpoint.CreateSecretKey, Endpoint.ListSalesChannels>(Endpoint.ListSalesChannels, () => ({
			wildcard: true,
			key: [Endpoint.ListSalesChannels]
		})),
		createMutation<Endpoint.CreateSecretKey, Endpoint.ListPointsOfSale>(Endpoint.ListPointsOfSale, () => ({
			wildcard: true,
			key: [Endpoint.ListPointsOfSale]
		}))
	)
}

export const useUpdateSecretKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe
): EnhancedMutationApi<Endpoint.UpdateSecretKey, Subscribe> => {
	return useBaseUpdateSecretKey(
		subscribe,
		undefined,
		createMutation<Endpoint.UpdateSecretKey, Endpoint.GetSalesChannel>(Endpoint.GetSalesChannel, () => ({
			wildcard: true,
			key: [Endpoint.GetSalesChannel]
		})),
		createMutation<Endpoint.UpdateSecretKey, Endpoint.GetPointOfSale>(Endpoint.GetPointOfSale, () => ({
			wildcard: true,
			key: [Endpoint.GetPointOfSale]
		}))
	)
}

export const useUpdateGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe
): EnhancedMutationApi<Endpoint.UpdateGiftCard, Subscribe> => {
	return useBaseUpdateGiftCard(
		subscribe,
		undefined,
		createMutation<Endpoint.UpdateGiftCard, Endpoint.ListGiftCards>(Endpoint.ListGiftCards, () => ({
			wildcard: true,
			key: [Endpoint.ListGiftCards]
		})),
		createMutation<Endpoint.UpdateGiftCard, Endpoint.GetGiftCard>(
			Endpoint.GetGiftCard,
			({ path: { giftCardUuid } }) => ({ path: { giftCardUuid } })
		)
	)
}

export const useUpdateLoyaltyCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	opts?: { customerUuid?: string }
): EnhancedMutationApi<Endpoint.UpdateLoyaltyCard, Subscribe> => {
	return useBaseUpdateLoyaltyCard(
		subscribe,
		undefined,
		createMutation<Endpoint.UpdateLoyaltyCard, Endpoint.ListLoyaltyCards>(Endpoint.ListLoyaltyCards, () => ({
			wildcard: true,
			key: [Endpoint.ListLoyaltyCards]
		})),
		createMutation<Endpoint.UpdateLoyaltyCard, Endpoint.GetLoyaltyCard>(
			Endpoint.GetLoyaltyCard,
			({ path: { cardUuid } }) => ({ path: { cardUuid } })
		),
		...(opts?.customerUuid
			? [
					createMutation<Endpoint.UpdateLoyaltyCard, Endpoint.GetCustomer>(Endpoint.GetCustomer, () => ({
						path: { customerUuid: opts.customerUuid! }
					}))
				]
			: [])
	)
}

export const useDeleteLoyaltyCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteLoyaltyCard>
): EnhancedMutationApi<Endpoint.DeleteLoyaltyCard, Subscribe> => {
	return useBaseDeleteLoyaltyCard(
		subscribe,
		config,
		createMutation<Endpoint.DeleteLoyaltyCard, Endpoint.ListLoyaltyCards>(Endpoint.ListLoyaltyCards, () => ({
			wildcard: true,
			key: [Endpoint.ListLoyaltyCards]
		}))
	)
}

export const useInitAssetUpload = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.InitAssetUpload>
): EnhancedMutationApi<Endpoint.InitAssetUpload, Subscribe> => {
	return useBaseInitAssetUpload(subscribe, config)
}

export const useCompleteAssetUpload = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CompleteAssetUpload>
): EnhancedMutationApi<Endpoint.CompleteAssetUpload, Subscribe> => {
	return useBaseCompleteAssetUpload(
		subscribe,
		config,
		createMutation<Endpoint.CompleteAssetUpload, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
			wildcard: true,
			key: [Endpoint.ListAssets]
		})),
		createMutation<Endpoint.CompleteAssetUpload, Endpoint.ListAssetTags>(Endpoint.ListAssetTags, () => ({
			wildcard: true,
			key: [Endpoint.ListAssetTags]
		}))
	)
}

export const useDeleteAsset = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteAsset>
): EnhancedMutationApi<Endpoint.DeleteAsset, Subscribe> => {
	return useBaseDeleteAsset(
		subscribe,
		config,
		createMutation<Endpoint.DeleteAsset, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
			wildcard: true,
			key: [Endpoint.ListAssets]
		}))
	)
}

export const useUpdateAsset = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateAsset>
): EnhancedMutationApi<Endpoint.UpdateAsset, Subscribe> => {
	return useBaseUpdateAsset(
		subscribe,
		config,
		createMutation<Endpoint.UpdateAsset, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
			wildcard: true,
			key: [Endpoint.ListAssets]
		})),
		createMutation<Endpoint.UpdateAsset, Endpoint.GetAsset>(Endpoint.GetAsset, ({ path: { assetUuid } }) => ({
			path: { assetUuid }
		})),
		createMutation<Endpoint.UpdateAsset, Endpoint.ListAssetTags>(Endpoint.ListAssetTags, () => ({
			wildcard: true,
			key: [Endpoint.ListAssetTags]
		}))
	)
}

export const useRemoveTagFromAssets = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RemoveTagFromAssets>
): EnhancedMutationApi<Endpoint.RemoveTagFromAssets, Subscribe> => {
	return useBaseRemoveTagFromAssets(
		subscribe,
		config,
		createMutation<Endpoint.RemoveTagFromAssets, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
			wildcard: true,
			key: [Endpoint.ListAssets]
		})),
		createMutation<Endpoint.RemoveTagFromAssets, Endpoint.ListAssetTags>(Endpoint.ListAssetTags, () => ({
			wildcard: true,
			key: [Endpoint.ListAssetTags]
		}))
	)
}

export const useCreateAssetTag = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateAssetTag>
): EnhancedMutationApi<Endpoint.CreateAssetTag, Subscribe> => {
	return useBaseCreateAssetTag(
		subscribe,
		config,
		createMutation<Endpoint.CreateAssetTag, Endpoint.ListAssetTags>(Endpoint.ListAssetTags, () => ({
			wildcard: true,
			key: [Endpoint.ListAssetTags]
		}))
	)
}

export const useRenameAssetTag = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RenameAssetTag>
): EnhancedMutationApi<Endpoint.RenameAssetTag, Subscribe> => {
	return useBaseRenameAssetTag(
		subscribe,
		config,
		createMutation<Endpoint.RenameAssetTag, Endpoint.ListAssetTags>(Endpoint.ListAssetTags, () => ({
			wildcard: true,
			key: [Endpoint.ListAssetTags]
		})),
		createMutation<Endpoint.RenameAssetTag, Endpoint.ListAssets>(Endpoint.ListAssets, () => ({
			wildcard: true,
			key: [Endpoint.ListAssets]
		}))
	)
}

const productImageMutations = (productUuid: string) => [
	createMutation<Endpoint.AttachProductImage, Endpoint.ListProductImages>(Endpoint.ListProductImages, () => ({
		path: { productUuid }
	})),
	createMutation<Endpoint.AttachProductImage, Endpoint.GetProduct>(Endpoint.GetProduct, () => ({
		path: { productUuid }
	})),
	createMutation<Endpoint.AttachProductImage, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
		wildcard: true,
		key: [Endpoint.ListProducts]
	}))
]

export const useAttachProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	productUuid: string,
	config?: MutationConfig<Endpoint.AttachProductImage>
): EnhancedMutationApi<Endpoint.AttachProductImage, Subscribe> => {
	return useBaseAttachProductImage(subscribe, config, ...productImageMutations(productUuid))
}

export const useReorderProductImages = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	productUuid: string,
	config?: MutationConfig<Endpoint.ReorderProductImages>
): EnhancedMutationApi<Endpoint.ReorderProductImages, Subscribe> => {
	return useBaseReorderProductImages(
		subscribe,
		config,
		createMutation<Endpoint.ReorderProductImages, Endpoint.ListProductImages>(Endpoint.ListProductImages, () => ({
			path: { productUuid }
		})),
		createMutation<Endpoint.ReorderProductImages, Endpoint.GetProduct>(Endpoint.GetProduct, () => ({
			path: { productUuid }
		}))
	)
}

export const useDeleteProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	productUuid: string,
	config?: MutationConfig<Endpoint.DeleteProductImage>
): EnhancedMutationApi<Endpoint.DeleteProductImage, Subscribe> => {
	return useBaseDeleteProductImage(
		subscribe,
		config,
		createMutation<Endpoint.DeleteProductImage, Endpoint.ListProductImages>(Endpoint.ListProductImages, () => ({
			path: { productUuid }
		})),
		createMutation<Endpoint.DeleteProductImage, Endpoint.GetProduct>(Endpoint.GetProduct, () => ({
			path: { productUuid }
		})),
		createMutation<Endpoint.DeleteProductImage, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		}))
	)
}

export const useSetPrimaryProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	productUuid: string,
	config?: MutationConfig<Endpoint.SetPrimaryProductImage>
): EnhancedMutationApi<Endpoint.SetPrimaryProductImage, Subscribe> => {
	return useBaseSetPrimaryProductImage(
		subscribe,
		config,
		createMutation<Endpoint.SetPrimaryProductImage, Endpoint.ListProductImages>(Endpoint.ListProductImages, () => ({
			path: { productUuid }
		})),
		createMutation<Endpoint.SetPrimaryProductImage, Endpoint.GetProduct>(Endpoint.GetProduct, () => ({
			path: { productUuid }
		})),
		createMutation<Endpoint.SetPrimaryProductImage, Endpoint.ListProducts>(Endpoint.ListProducts, () => ({
			wildcard: true,
			key: [Endpoint.ListProducts]
		}))
	)
}

export const useReplaceFeedLayoutBlocks = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	marketingChannelUuid: string,
	config?: MutationConfig<Endpoint.ReplaceFeedLayoutBlocks>
): EnhancedMutationApi<Endpoint.ReplaceFeedLayoutBlocks, Subscribe> => {
	return useBaseReplaceFeedLayoutBlocks(
		subscribe,
		config,
		createMutation<Endpoint.ReplaceFeedLayoutBlocks, Endpoint.GetFeedLayout>(Endpoint.GetFeedLayout, () => ({
			path: { marketingChannelUuid },
			query: {}
		}))
	)
}

export const useCreateMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateMarketingChannel>
): EnhancedMutationApi<Endpoint.CreateMarketingChannel, Subscribe> => {
	return useBaseCreateMarketingChannel(
		subscribe,
		config,
		createMutation<Endpoint.CreateMarketingChannel, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListMarketingChannels]
			})
		)
	)
}

export const useUpdateMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	marketingChannelUuid: string,
	config?: MutationConfig<Endpoint.UpdateMarketingChannel>
): EnhancedMutationApi<Endpoint.UpdateMarketingChannel, Subscribe> => {
	return useBaseUpdateMarketingChannel(
		subscribe,
		config,
		createMutation<Endpoint.UpdateMarketingChannel, Endpoint.GetMarketingChannel>(
			Endpoint.GetMarketingChannel,
			() => ({
				path: { marketingChannelUuid }
			})
		),
		createMutation<Endpoint.UpdateMarketingChannel, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListMarketingChannels]
			})
		)
	)
}

export const useDeleteMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteMarketingChannel>
): EnhancedMutationApi<Endpoint.DeleteMarketingChannel, Subscribe> => {
	return useBaseDeleteMarketingChannel(
		subscribe,
		config,
		createMutation<Endpoint.DeleteMarketingChannel, Endpoint.ListMarketingChannels>(
			Endpoint.ListMarketingChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListMarketingChannels]
			})
		)
	)
}

// Reordering / re-placing content on a channel's layout changes both the layout editor's view and
// the resolved feed preview, so invalidate both for the edited channel.
export const useReplaceChannelLayout = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	marketingChannelUuid: string,
	config?: MutationConfig<Endpoint.ReplaceChannelLayout>
): EnhancedMutationApi<Endpoint.ReplaceChannelLayout, Subscribe> => {
	return useBaseReplaceChannelLayout(
		subscribe,
		config,
		createMutation<Endpoint.ReplaceChannelLayout, Endpoint.GetChannelLayout>(Endpoint.GetChannelLayout, () => ({
			wildcard: true,
			key: [Endpoint.GetChannelLayout]
		})),
		createMutation<Endpoint.ReplaceChannelLayout, Endpoint.GetChannelResolvedContent>(
			Endpoint.GetChannelResolvedContent,
			() => ({ wildcard: true, key: [Endpoint.GetChannelResolvedContent] })
		)
	)
}

export const useCreateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateMarketingContent>
): EnhancedMutationApi<Endpoint.CreateMarketingContent, Subscribe> => {
	return useBaseCreateMarketingContent(
		subscribe,
		config,
		createMutation<Endpoint.CreateMarketingContent, Endpoint.ListMarketingContent>(
			Endpoint.ListMarketingContent,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingContent] })
		)
	)
}

export const useUpdateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	contentUuid: string,
	config?: MutationConfig<Endpoint.UpdateMarketingContent>
): EnhancedMutationApi<Endpoint.UpdateMarketingContent, Subscribe> => {
	return useBaseUpdateMarketingContent(
		subscribe,
		config,
		createMutation<Endpoint.UpdateMarketingContent, Endpoint.GetMarketingContent>(
			Endpoint.GetMarketingContent,
			() => ({ path: { contentUuid } })
		),
		createMutation<Endpoint.UpdateMarketingContent, Endpoint.ListMarketingContent>(
			Endpoint.ListMarketingContent,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingContent] })
		),
		// Editing content (schedule/status/blocks) changes the resolved feed of every channel it's
		// placed on, so refresh the layout preview.
		createMutation<Endpoint.UpdateMarketingContent, Endpoint.GetChannelResolvedContent>(
			Endpoint.GetChannelResolvedContent,
			() => ({ wildcard: true, key: [Endpoint.GetChannelResolvedContent] })
		)
	)
}

export const useDeleteMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteMarketingContent>
): EnhancedMutationApi<Endpoint.DeleteMarketingContent, Subscribe> => {
	return useBaseDeleteMarketingContent(
		subscribe,
		config,
		createMutation<Endpoint.DeleteMarketingContent, Endpoint.ListMarketingContent>(
			Endpoint.ListMarketingContent,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingContent] })
		),
		// Deleting content removes it from every layout it was placed on, changing those feeds.
		createMutation<Endpoint.DeleteMarketingContent, Endpoint.GetChannelResolvedContent>(
			Endpoint.GetChannelResolvedContent,
			() => ({ wildcard: true, key: [Endpoint.GetChannelResolvedContent] })
		)
	)
}

export const useReplaceMarketingContentBlocks = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	contentUuid: string,
	config?: MutationConfig<Endpoint.ReplaceMarketingContentBlocks>
): EnhancedMutationApi<Endpoint.ReplaceMarketingContentBlocks, Subscribe> => {
	return useBaseReplaceMarketingContentBlocks(
		subscribe,
		config,
		createMutation<Endpoint.ReplaceMarketingContentBlocks, Endpoint.GetMarketingContent>(
			Endpoint.GetMarketingContent,
			() => ({ path: { contentUuid } })
		),
		createMutation<Endpoint.ReplaceMarketingContentBlocks, Endpoint.ListMarketingContent>(
			Endpoint.ListMarketingContent,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingContent] })
		),
		// Block edits change the resolved feed of every channel this content is placed on.
		createMutation<Endpoint.ReplaceMarketingContentBlocks, Endpoint.GetChannelResolvedContent>(
			Endpoint.GetChannelResolvedContent,
			() => ({ wildcard: true, key: [Endpoint.GetChannelResolvedContent] })
		)
	)
}

export const useDuplicateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DuplicateMarketingContent>
): EnhancedMutationApi<Endpoint.DuplicateMarketingContent, Subscribe> => {
	return useBaseDuplicateMarketingContent(
		subscribe,
		config,
		createMutation<Endpoint.DuplicateMarketingContent, Endpoint.ListMarketingContent>(
			Endpoint.ListMarketingContent,
			() => ({ wildcard: true, key: [Endpoint.ListMarketingContent] })
		)
	)
}

export const useAdjustInventoryLevel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AdjustInventoryLevel>
): EnhancedMutationApi<Endpoint.AdjustInventoryLevel, Subscribe> => {
	return useBaseAdjustInventoryLevel(
		subscribe,
		config,
		createMutation<Endpoint.AdjustInventoryLevel, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		),
		createMutation<Endpoint.AdjustInventoryLevel, Endpoint.GetInventorySummary>(
			Endpoint.GetInventorySummary,
			() => ({
				wildcard: true,
				key: [Endpoint.GetInventorySummary]
			})
		)
	)
}

// Returns the Teya authorize URL; no server state changes, so no cache invalidation is needed.
export const useCreateTeyaConnectUrl = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateTeyaConnectUrl>
): EnhancedMutationApi<Endpoint.CreateTeyaConnectUrl, Subscribe> => {
	return useBaseCreateTeyaConnectUrl(subscribe, config)
}

export const useDisconnectTeya = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DisconnectTeya>
): EnhancedMutationApi<Endpoint.DisconnectTeya, Subscribe> => {
	return useBaseDisconnectTeya(
		subscribe,
		config,
		createMutation<Endpoint.DisconnectTeya, Endpoint.GetTeyaIntegration>(Endpoint.GetTeyaIntegration, () => ({
			wildcard: true,
			key: [Endpoint.GetTeyaIntegration]
		}))
	)
}

export const useUpdateInventoryLevel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventoryLevel>
): EnhancedMutationApi<Endpoint.UpdateInventoryLevel, Subscribe> => {
	return useBaseUpdateInventoryLevel(
		subscribe,
		config,
		createMutation<Endpoint.UpdateInventoryLevel, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		),
		createMutation<Endpoint.UpdateInventoryLevel, Endpoint.GetInventorySummary>(
			Endpoint.GetInventorySummary,
			() => ({
				wildcard: true,
				key: [Endpoint.GetInventorySummary]
			})
		)
	)
}

export const useUpdateInventorySettings = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventorySettings>
): EnhancedMutationApi<Endpoint.UpdateInventorySettings, Subscribe> => {
	return useBaseUpdateInventorySettings(
		subscribe,
		config,
		createMutation<Endpoint.UpdateInventorySettings, Endpoint.GetInventorySettings>(
			Endpoint.GetInventorySettings,
			() => ({
				wildcard: true,
				key: [Endpoint.GetInventorySettings]
			})
		)
	)
}

export const useCreateInventoryLocation = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInventoryLocation>
): EnhancedMutationApi<Endpoint.CreateInventoryLocation, Subscribe> => {
	return useBaseCreateInventoryLocation(
		subscribe,
		config,
		createMutation<Endpoint.CreateInventoryLocation, Endpoint.ListInventoryLocations>(
			Endpoint.ListInventoryLocations,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLocations]
			})
		)
	)
}

export const useUpdateInventoryLocation = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventoryLocation>
): EnhancedMutationApi<Endpoint.UpdateInventoryLocation, Subscribe> => {
	return useBaseUpdateInventoryLocation(
		subscribe,
		config,
		createMutation<Endpoint.UpdateInventoryLocation, Endpoint.ListInventoryLocations>(
			Endpoint.ListInventoryLocations,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLocations]
			})
		),
		createMutation<Endpoint.UpdateInventoryLocation, Endpoint.GetInventoryLocation>(
			Endpoint.GetInventoryLocation,
			({ path: { inventoryLocationUuid } }) => ({
				path: { inventoryLocationUuid }
			})
		),
		createMutation<Endpoint.UpdateInventoryLocation, Endpoint.ListSalesChannels>(
			Endpoint.ListSalesChannels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListSalesChannels]
			})
		)
	)
}

export const useProvisionInventoryLocationLevels = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ProvisionInventoryLocationLevels>
): EnhancedMutationApi<Endpoint.ProvisionInventoryLocationLevels, Subscribe> => {
	return useBaseProvisionInventoryLocationLevels(subscribe, config)
}

export const useCreateSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSupplier>
): EnhancedMutationApi<Endpoint.CreateSupplier, Subscribe> => {
	return useBaseCreateSupplier(
		subscribe,
		config,
		createMutation<Endpoint.CreateSupplier, Endpoint.ListSuppliers>(Endpoint.ListSuppliers, () => ({
			wildcard: true,
			key: [Endpoint.ListSuppliers]
		}))
	)
}

export const useUpdateSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSupplier>
): EnhancedMutationApi<Endpoint.UpdateSupplier, Subscribe> => {
	return useBaseUpdateSupplier(
		subscribe,
		config,
		createMutation<Endpoint.UpdateSupplier, Endpoint.ListSuppliers>(Endpoint.ListSuppliers, () => ({
			wildcard: true,
			key: [Endpoint.ListSuppliers]
		})),
		createMutation<Endpoint.UpdateSupplier, Endpoint.GetSupplier>(
			Endpoint.GetSupplier,
			({ path: { supplierUuid } }) => ({
				path: { supplierUuid }
			})
		)
	)
}

export const useReceivePurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReceivePurchaseOrder>
): EnhancedMutationApi<Endpoint.ReceivePurchaseOrder, Subscribe> => {
	return useBaseReceivePurchaseOrder(
		subscribe,
		config,
		createMutation<Endpoint.ReceivePurchaseOrder, Endpoint.ListPurchaseOrders>(Endpoint.ListPurchaseOrders, () => ({
			wildcard: true,
			key: [Endpoint.ListPurchaseOrders]
		})),
		createMutation<Endpoint.ReceivePurchaseOrder, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

export const useCreatePurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePurchaseOrder>
): EnhancedMutationApi<Endpoint.CreatePurchaseOrder, Subscribe> => {
	return useBaseCreatePurchaseOrder(
		subscribe,
		config,
		createMutation<Endpoint.CreatePurchaseOrder, Endpoint.ListPurchaseOrders>(Endpoint.ListPurchaseOrders, () => ({
			wildcard: true,
			key: [Endpoint.ListPurchaseOrders]
		}))
	)
}

export const useCreateReplenishmentPurchaseOrders = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateReplenishmentPurchaseOrders>
): EnhancedMutationApi<Endpoint.CreateReplenishmentPurchaseOrders, Subscribe> => {
	return useBaseCreateReplenishmentPurchaseOrders(
		subscribe,
		config,
		createMutation<Endpoint.CreateReplenishmentPurchaseOrders, Endpoint.ListPurchaseOrders>(
			Endpoint.ListPurchaseOrders,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPurchaseOrders]
			})
		)
	)
}

export const useMarkPurchaseOrderOrdered = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.MarkPurchaseOrderOrdered>
): EnhancedMutationApi<Endpoint.MarkPurchaseOrderOrdered, Subscribe> => {
	return useBaseMarkPurchaseOrderOrdered(
		subscribe,
		config,
		createMutation<Endpoint.MarkPurchaseOrderOrdered, Endpoint.ListPurchaseOrders>(
			Endpoint.ListPurchaseOrders,
			() => ({
				wildcard: true,
				key: [Endpoint.ListPurchaseOrders]
			})
		),
		createMutation<Endpoint.MarkPurchaseOrderOrdered, Endpoint.GetPurchaseOrder>(
			Endpoint.GetPurchaseOrder,
			({ path: { purchaseOrderUuid } }) => ({ path: { purchaseOrderUuid } })
		)
	)
}

export const useCancelPurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelPurchaseOrder>
): EnhancedMutationApi<Endpoint.CancelPurchaseOrder, Subscribe> => {
	return useBaseCancelPurchaseOrder(
		subscribe,
		config,
		createMutation<Endpoint.CancelPurchaseOrder, Endpoint.ListPurchaseOrders>(Endpoint.ListPurchaseOrders, () => ({
			wildcard: true,
			key: [Endpoint.ListPurchaseOrders]
		})),
		createMutation<Endpoint.CancelPurchaseOrder, Endpoint.GetPurchaseOrder>(
			Endpoint.GetPurchaseOrder,
			({ path: { purchaseOrderUuid } }) => ({ path: { purchaseOrderUuid } })
		)
	)
}

export const useShipInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ShipInventoryTransfer>
): EnhancedMutationApi<Endpoint.ShipInventoryTransfer, Subscribe> => {
	return useBaseShipInventoryTransfer(
		subscribe,
		config,
		createMutation<Endpoint.ShipInventoryTransfer, Endpoint.ListInventoryTransfers>(
			Endpoint.ListInventoryTransfers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryTransfers]
			})
		),
		createMutation<Endpoint.ShipInventoryTransfer, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

export const useReceiveInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReceiveInventoryTransfer>
): EnhancedMutationApi<Endpoint.ReceiveInventoryTransfer, Subscribe> => {
	return useBaseReceiveInventoryTransfer(
		subscribe,
		config,
		createMutation<Endpoint.ReceiveInventoryTransfer, Endpoint.ListInventoryTransfers>(
			Endpoint.ListInventoryTransfers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryTransfers]
			})
		),
		createMutation<Endpoint.ReceiveInventoryTransfer, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

export const useCreateInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInventoryTransfer>
): EnhancedMutationApi<Endpoint.CreateInventoryTransfer, Subscribe> => {
	return useBaseCreateInventoryTransfer(
		subscribe,
		config,
		createMutation<Endpoint.CreateInventoryTransfer, Endpoint.ListInventoryTransfers>(
			Endpoint.ListInventoryTransfers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryTransfers]
			})
		)
	)
}

export const useCancelInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelInventoryTransfer>
): EnhancedMutationApi<Endpoint.CancelInventoryTransfer, Subscribe> => {
	return useBaseCancelInventoryTransfer(
		subscribe,
		config,
		createMutation<Endpoint.CancelInventoryTransfer, Endpoint.ListInventoryTransfers>(
			Endpoint.ListInventoryTransfers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryTransfers]
			})
		),
		createMutation<Endpoint.CancelInventoryTransfer, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		),
		createMutation<Endpoint.CancelInventoryTransfer, Endpoint.GetInventoryTransfer>(
			Endpoint.GetInventoryTransfer,
			({ path: { inventoryTransferUuid } }) => ({ path: { inventoryTransferUuid } })
		)
	)
}

export const useUpdateProductVariant = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductVariant>
): EnhancedMutationApi<Endpoint.UpdateProductVariant, Subscribe> => {
	return useBaseUpdateProductVariant(
		subscribe,
		config,
		createMutation<Endpoint.UpdateProductVariant, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		),
		createMutation<Endpoint.UpdateProductVariant, Endpoint.GetProduct>(
			Endpoint.GetProduct,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		),
		createMutation<Endpoint.UpdateProductVariant, Endpoint.ListProductVariants>(
			Endpoint.ListProductVariants,
			({ path: { productUuid } }) => ({ path: { productUuid } })
		)
	)
}

export const useUpsertVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpsertVariantSupplier>
): EnhancedMutationApi<Endpoint.UpsertVariantSupplier, Subscribe> => {
	return useBaseUpsertVariantSupplier(
		subscribe,
		config,
		createMutation<Endpoint.UpsertVariantSupplier, Endpoint.ListVariantSuppliers>(
			Endpoint.ListVariantSuppliers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListVariantSuppliers]
			})
		),
		createMutation<Endpoint.UpsertVariantSupplier, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

export const useSetPreferredVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetPreferredVariantSupplier>
): EnhancedMutationApi<Endpoint.SetPreferredVariantSupplier, Subscribe> => {
	return useBaseSetPreferredVariantSupplier(
		subscribe,
		config,
		createMutation<Endpoint.SetPreferredVariantSupplier, Endpoint.ListVariantSuppliers>(
			Endpoint.ListVariantSuppliers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListVariantSuppliers]
			})
		),
		createMutation<Endpoint.SetPreferredVariantSupplier, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

export const useDeleteVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteVariantSupplier>
): EnhancedMutationApi<Endpoint.DeleteVariantSupplier, Subscribe> => {
	return useBaseDeleteVariantSupplier(
		subscribe,
		config,
		createMutation<Endpoint.DeleteVariantSupplier, Endpoint.ListVariantSuppliers>(
			Endpoint.ListVariantSuppliers,
			() => ({
				wildcard: true,
				key: [Endpoint.ListVariantSuppliers]
			})
		),
		createMutation<Endpoint.DeleteVariantSupplier, Endpoint.ListInventoryLevels>(
			Endpoint.ListInventoryLevels,
			() => ({
				wildcard: true,
				key: [Endpoint.ListInventoryLevels]
			})
		)
	)
}

// Registers a PAX serial with Rapyd to start pairing; no FastTrack server state changes (the device
// token is stored later via the integration override), so no cache invalidation is needed.
export const useRegisterRapydDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RegisterRapydDevice>
): EnhancedMutationApi<Endpoint.RegisterRapydDevice, Subscribe> => {
	return useBaseRegisterRapydDevice(subscribe, config)
}

// Generic integration config mutations. Each returns the fresh IntegrationConfig, but we also
// invalidate the config + summary queries so any other view stays consistent.
const integrationConfigInvalidations = <T extends Endpoint>() => [
	createMutation<T, Endpoint.GetIntegrationConfig>(Endpoint.GetIntegrationConfig, (input: any) => ({
		path: { provider: input.path.provider }
	})),
	createMutation<T, Endpoint.ListIntegrations>(Endpoint.ListIntegrations, () => ({
		wildcard: true,
		key: [Endpoint.ListIntegrations]
	}))
]

export const usePutIntegrationGlobalConfig = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.PutIntegrationGlobalConfig>
): EnhancedMutationApi<Endpoint.PutIntegrationGlobalConfig, Subscribe> => {
	return useBasePutIntegrationGlobalConfig(
		subscribe,
		config,
		...integrationConfigInvalidations<Endpoint.PutIntegrationGlobalConfig>()
	)
}

export const useCreateIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateIntegrationOverride>
): EnhancedMutationApi<Endpoint.CreateIntegrationOverride, Subscribe> => {
	return useBaseCreateIntegrationOverride(
		subscribe,
		config,
		...integrationConfigInvalidations<Endpoint.CreateIntegrationOverride>()
	)
}

export const useUpdateIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateIntegrationOverride>
): EnhancedMutationApi<Endpoint.UpdateIntegrationOverride, Subscribe> => {
	return useBaseUpdateIntegrationOverride(
		subscribe,
		config,
		...integrationConfigInvalidations<Endpoint.UpdateIntegrationOverride>()
	)
}

export const useDeleteIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteIntegrationOverride>
): EnhancedMutationApi<Endpoint.DeleteIntegrationOverride, Subscribe> => {
	return useBaseDeleteIntegrationOverride(
		subscribe,
		config,
		...integrationConfigInvalidations<Endpoint.DeleteIntegrationOverride>()
	)
}

// Neither mutation gets an immediate answer back from the device — they just push a WS nudge —
// so invalidating (rather than optimistically updating) `GetPointOfSaleDeviceStatus` just makes
// sure a subsequent `refetchInterval` tick isn't serving a stale cached response.
export const useRefreshPointOfSaleDeviceStatus = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RefreshPointOfSaleDeviceStatus>
): EnhancedMutationApi<Endpoint.RefreshPointOfSaleDeviceStatus, Subscribe> => {
	return useBaseRefreshPointOfSaleDeviceStatus(
		subscribe,
		config,
		createMutation<Endpoint.RefreshPointOfSaleDeviceStatus, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useNotifyPointOfSaleAppUpdate = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.NotifyPointOfSaleAppUpdate>
): EnhancedMutationApi<Endpoint.NotifyPointOfSaleAppUpdate, Subscribe> => {
	return useBaseNotifyPointOfSaleAppUpdate(
		subscribe,
		config,
		createMutation<Endpoint.NotifyPointOfSaleAppUpdate, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useRestartPointOfSaleApp = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RestartPointOfSaleApp>
): EnhancedMutationApi<Endpoint.RestartPointOfSaleApp, Subscribe> => {
	return useBaseRestartPointOfSaleApp(subscribe, config)
}

export const useLockPointOfSaleKiosk = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.LockPointOfSaleKiosk>
): EnhancedMutationApi<Endpoint.LockPointOfSaleKiosk, Subscribe> => {
	return useBaseLockPointOfSaleKiosk(subscribe, config)
}

// GMS mutations all mutate the same `amapi` block on `GetPointOfSaleDeviceStatus` — invalidate it
// so the panel picks up the new deviceName/state/policy right after each action resolves.
export const useCreatePointOfSaleGmsEnrollment = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSaleGmsEnrollment>
): EnhancedMutationApi<Endpoint.CreatePointOfSaleGmsEnrollment, Subscribe> => {
	return useBaseCreatePointOfSaleGmsEnrollment(
		subscribe,
		config,
		createMutation<Endpoint.CreatePointOfSaleGmsEnrollment, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useSyncPointOfSaleGmsPolicy = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SyncPointOfSaleGmsPolicy>
): EnhancedMutationApi<Endpoint.SyncPointOfSaleGmsPolicy, Subscribe> => {
	return useBaseSyncPointOfSaleGmsPolicy(
		subscribe,
		config,
		createMutation<Endpoint.SyncPointOfSaleGmsPolicy, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useRebootPointOfSaleGmsDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RebootPointOfSaleGmsDevice>
): EnhancedMutationApi<Endpoint.RebootPointOfSaleGmsDevice, Subscribe> => {
	return useBaseRebootPointOfSaleGmsDevice(subscribe, config)
}

export const useDeletePointOfSaleGmsDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePointOfSaleGmsDevice>
): EnhancedMutationApi<Endpoint.DeletePointOfSaleGmsDevice, Subscribe> => {
	return useBaseDeletePointOfSaleGmsDevice(
		subscribe,
		config,
		createMutation<Endpoint.DeletePointOfSaleGmsDevice, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useEnrollPointOfSaleDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.EnrollPointOfSaleDevice>
): EnhancedMutationApi<Endpoint.EnrollPointOfSaleDevice, Subscribe> => {
	return useBaseEnrollPointOfSaleDevice(
		subscribe,
		config,
		createMutation<Endpoint.EnrollPointOfSaleDevice, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}

export const useUnenrollPointOfSaleDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnenrollPointOfSaleDevice>
): EnhancedMutationApi<Endpoint.UnenrollPointOfSaleDevice, Subscribe> => {
	return useBaseUnenrollPointOfSaleDevice(
		subscribe,
		config,
		createMutation<Endpoint.UnenrollPointOfSaleDevice, Endpoint.GetPointOfSaleDeviceStatus>(
			Endpoint.GetPointOfSaleDeviceStatus,
			({ path: { pointOfSaleUuid } }) => ({ path: { pointOfSaleUuid } })
		)
	)
}
